import { useState, type ComponentType, type SVGProps } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PLANBASE_CONFIG } from '../../config';
import { cn } from '../../lib/utils';
import SidebarProfilePopover from '../nav/SidebarProfilePopover';
import {
  BookmarkIcon,
  ChatBubbleLeftIcon,
  ChevronRightIcon,
  FolderIcon,
  MapIcon,
  PlusIcon,
  RectangleGroupIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

type OrbiChatSession = {
  id: string;
  title: string;
  updated_at?: string;
};

type OrbiWorkflowDraft = {
  id: string;
  title: string;
  updatedAt?: string;
};

interface OrbiSidebarProps {
  /**
   * Kept for API compatibility with DashboardLayout, but resolution
   * now lives inside SidebarProfilePopover — no props needed.
   */
  displayName?: string;
  userEmail?: string;
  userAvatarUrl?: string;
  isAdmin?: boolean;
  sessions?: OrbiChatSession[];
  activeSessionId?: string;
  workflows?: OrbiWorkflowDraft[];
  activeWorkflowId?: string;
  onNewChat?: () => void;
  onSelectSession?: (id: string) => void;
  onDeleteSession?: (id: string) => void;
  onSelectWorkflow?: (id: string) => void;
  onDeleteWorkflow?: (id: string) => void;
  onLogout?: () => void;
}

const navItems: { label: string; href: string; icon: IconComponent }[] = [
  { label: 'Discover', href: '/discover', icon: MapIcon },
  { label: 'Portfolio', href: '/portfolio', icon: FolderIcon },
  { label: 'Orbi AI', href: '/orbi', icon: SparklesIcon },
  { label: 'Collection', href: '/collections', icon: BookmarkIcon },
];

export default function OrbiSidebar({
  sessions = [],
  activeSessionId,
  workflows = [],
  activeWorkflowId,
  onNewChat,
  onSelectSession,
  onDeleteSession,
  onSelectWorkflow,
  onDeleteWorkflow,
  onLogout,
}: OrbiSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Recent = chats + workflows merged and sorted by recency.
  const recentItems = [
    ...sessions.map((s) => ({
      kind: 'chat' as const,
      id: s.id,
      title: s.title || 'Untitled chat',
      ts: s.updated_at ? new Date(s.updated_at).getTime() : 0,
    })),
    ...workflows.map((w) => ({
      kind: 'workflow' as const,
      id: w.id,
      title: w.title || 'Untitled workflow',
      ts: w.updatedAt ? new Date(w.updatedAt).getTime() : 0,
    })),
  ].sort((a, b) => b.ts - a.ts);

  const [recentOpen, setRecentOpen] = useState(recentItems.length > 0);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleNewChat = () => {
    if (onNewChat) onNewChat();
    else navigate('/orbi');
  };

  const handleDelete = (kind: 'chat' | 'workflow', id: string) => {
    const key = `${kind}:${id}`;
    if (confirmDeleteId === key) {
      if (kind === 'chat') onDeleteSession?.(id);
      else onDeleteWorkflow?.(id);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(key);
      setTimeout(
        () => setConfirmDeleteId((current) => (current === key ? null : current)),
        3000,
      );
    }
  };

  return (
    <aside
      className="hidden md:flex md:sticky md:top-0 md:h-screen w-52 flex-shrink-0 flex-col justify-between bg-background p-4 font-display text-sm"
      aria-label="Orbi navigation"
    >
      <div className="flex flex-col gap-4 min-h-0">
        {/* Planbase logo — matches the standard Sidebar for consistency. */}
        <Link to="/discover" className="flex items-center gap-2 pb-1" aria-label="Planbase home">
          {PLANBASE_CONFIG.project.logo_url ? (
            <img
              src={PLANBASE_CONFIG.project.logo_url}
              alt="Planbase"
              className="h-7 w-7 flex-shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-orange-600">
              <SparklesIcon className="h-4 w-4 text-white" />
            </div>
          )}
          <span className="text-sm font-medium text-foreground">Planbase</span>
        </Link>

        <div className="flex flex-col gap-1 border-b border-border pb-2">
          <button
            type="button"
            onClick={handleNewChat}
            className="flex items-center gap-2 px-1 py-1.5 text-muted-foreground transition-colors hover:text-foreground"
          >
            <PlusIcon className="h-5 w-5 flex-shrink-0" />
            <span>New Chat</span>
          </button>

          {navItems.map((item) => {
            const active =
              location.pathname === item.href ||
              location.pathname.startsWith(item.href + '/');
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.href}
                to={item.href}
                className={cn(
                  'flex items-center gap-2 px-1 py-1.5 transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <ItemIcon className="h-5 w-5 flex-shrink-0" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </div>

        <div className="flex flex-1 flex-col min-h-0">
          <button
            type="button"
            onClick={() => setRecentOpen((v) => !v)}
            className="flex items-center justify-between px-1 py-1 text-xs text-muted-foreground"
            aria-expanded={recentOpen}
          >
            <span>Recent</span>
            <ChevronRightIcon
              className={cn('h-3.5 w-3.5 transition-transform', recentOpen && 'rotate-90')}
            />
          </button>

          {recentOpen && (
            <div className="mt-2 flex flex-1 flex-col gap-1.5 overflow-y-auto pr-1">
              {recentItems.length === 0 ? (
                <p className="px-1 py-1 text-xs text-muted-foreground/70">Nothing here yet</p>
              ) : (
                recentItems.map((item) => {
                  const isActive =
                    (item.kind === 'chat' && item.id === activeSessionId) ||
                    (item.kind === 'workflow' && item.id === activeWorkflowId);
                  const key = `${item.kind}:${item.id}`;
                  const isConfirming = confirmDeleteId === key;
                  const ItemIcon =
                    item.kind === 'workflow' ? RectangleGroupIcon : ChatBubbleLeftIcon;
                  const canDelete =
                    (item.kind === 'chat' && !!onDeleteSession) ||
                    (item.kind === 'workflow' && !!onDeleteWorkflow);
                  const label =
                    item.kind === 'workflow'
                      ? `Workflow — ${item.title}`
                      : `Chat — ${item.title}`;
                  return (
                    <div
                      key={key}
                      className={cn(
                        'group flex items-center gap-1 rounded-md pl-1 pr-1.5 py-1.5 transition-colors',
                        isActive && 'bg-muted',
                      )}
                    >
                      <ItemIcon
                        className={cn(
                          'h-3.5 w-3.5 flex-shrink-0',
                          item.kind === 'workflow'
                            ? 'text-primary/80'
                            : 'text-muted-foreground/60',
                        )}
                      />
                      <button
                        type="button"
                        onClick={() =>
                          item.kind === 'chat'
                            ? onSelectSession?.(item.id)
                            : onSelectWorkflow?.(item.id)
                        }
                        title={label}
                        className={cn(
                          'flex-1 min-w-0 truncate text-left text-xs transition-colors',
                          isActive
                            ? 'text-foreground'
                            : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {item.title}
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(item.kind, item.id)}
                          aria-label={
                            isConfirming
                              ? 'Confirm delete'
                              : item.kind === 'workflow'
                                ? 'Delete workflow'
                                : 'Delete chat'
                          }
                          className={cn(
                            'flex-shrink-0 rounded p-0.5 transition-opacity',
                            isConfirming
                              ? 'bg-destructive text-destructive-foreground opacity-100'
                              : 'text-muted-foreground opacity-0 hover:bg-muted-foreground/10 hover:text-destructive group-hover:opacity-100',
                          )}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Profile popover — shared component; behaviour + admin gate
          in one place (see src/components/nav/SidebarProfilePopover). */}
      <SidebarProfilePopover onLogout={onLogout} triggerVariant="card" />
    </aside>
  );
}

// Local MenuItem removed — SidebarProfilePopover ships its own.
